import type { NewTabOption, TabProps } from './tab';
import type { HTMLAttributes, ReactElement } from 'react';

import React, { Children, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InternalTab, NewTab, NewTabButton, Tab } from './tab';
import { useKeyboardControls } from '../../hooks/use-keyboard-controls';
import { useOnResize } from '../../hooks/use-on-resize';
import { ArrowLeftIcon, ArrowRightIcon } from '../../icons';
import { classNames } from '../../utils/class-names';
import typographyStyles from '../typography/typography.module.css';

import styles from './tabs.module.css';

export type TabsProps = {
  dynamic?: boolean;
  compact?: boolean;
  newTabLabel?: string;
  newTabOptions?: NewTabOption[];
  onSelectTab?: (tabIndex: number) => void;
  onAddTab?: (optionValue: NewTabOption['value']) => void;
  onRemoveTab?: (tabId: string) => void;
  selectedTabIndex?: number;
  children?: ReactElement<TabProps> | ReactElement<TabProps>[];
  contentWrapperProps?: HTMLAttributes<HTMLDivElement>;
} & HTMLAttributes<HTMLDivElement>;

/**
 * @param {boolean} [dynamic] Enables addition and deletion of tabs.
 * @default undefined
 * @param {boolean} [compact] Shows compact style with no arrows and fixed tab width.
 * @default undefined
 * @param {string} [newTabLabel] Label to be displayed in a new tab.
 * @default undefined
 * @param {Option[]} [newTabOptions] Options to be displayed in a new tab dropdown.
 * @default undefined
 * @param {Function} [onSelectTab] Callback to be executed when a tab is selected.
 * @default undefined
 * @param {Function} [onAddTab] Callback to be executed when a new tab is added.
 * @default undefined
 * @param {Function} [onRemoveTab] Callback to be executed when a tab is removed.
 * @default undefined
 * @param {number} [selectedTabIndex] Index of the tab to be selected on initial render. Can also be used to make the component controllable.
 * @default 0
 * @param {ReactElement<TabProps> | ReactElement<TabProps>[]} [children] Tabs to be displayed (use <Tab /> components).
 * @default undefined
 * @param {HTMLAttributes<HTMLDivElement>} [contentWrapperProps] Props to be passed to the content wrapper.
 * @default undefined
 */
export function Tabs({
  dynamic,
  compact,
  newTabLabel,
  newTabOptions,
  onSelectTab,
  onAddTab,
  onRemoveTab,
  selectedTabIndex = 0,
  children,
  contentWrapperProps,
  ...props
}: TabsProps) {
  const { t } = useTranslation();
  const mainWrapperRef = useRef<HTMLDivElement>(null);
  const tabsWrapperRef = useRef<HTMLDivElement>(null);
  const [currentTabIndex, setCurrentTabIndex] = useState(selectedTabIndex);
  const [arrowsVisible, setArrowsVisible] = useState(false);

  const tabs = Children.toArray(children) as ReactElement<TabProps>[];
  const hasOptions = Number(newTabOptions?.length) > 0;
  const allOptionsDisabled = newTabOptions?.every(({ disabled }) => disabled);
  const dropdownVisible = dynamic && hasOptions && !allOptionsDisabled;
  const newTabVisible = dropdownVisible && !arrowsVisible;
  const newTabButtonVisible = dropdownVisible && arrowsVisible;

  const getDomElements = () => {
    const wrapper = tabsWrapperRef.current;
    const tabs = Array.from(wrapper?.children || []) as HTMLButtonElement[];
    const currentlyFocusedTab = wrapper?.querySelector<HTMLButtonElement>(':focus');

    return { wrapper, tabs, currentlyFocusedTab };
  };

  useKeyboardControls(tabsWrapperRef, {
    onUp: () => {
      focusOnTab(0);
    },
    onDown: () => {
      focusOnTab(tabs.length - 1);
    },
    onLeft: () => {
      const previousTab = getDomElements().currentlyFocusedTab?.previousElementSibling as HTMLButtonElement;

      previousTab?.focus();
    },
    onRight: () => {
      const nextTab = getDomElements().currentlyFocusedTab?.nextElementSibling as HTMLButtonElement;

      nextTab?.focus();
    },
    onDelete: () => {
      dynamic && removeTab(currentTabIndex);
    },
  });

  const addTab = (selectedOption: NewTabOption) => {
    setCurrentTabIndex(tabs.length);
    onAddTab?.(selectedOption.value);
  };

  const focusOnTab = (tabIndex: number) => {
    getDomElements().tabs[tabIndex]?.focus();
  };

  const removeTab = (tabIndex: number) => {
    const clickedPreviousTab = tabIndex < currentTabIndex;
    const clickedSelectedLastTab = tabIndex === currentTabIndex && tabIndex === tabs.length - 1;
    const newIndex = clickedPreviousTab || clickedSelectedLastTab ? currentTabIndex - 1 : currentTabIndex;

    setCurrentTabIndex(newIndex < 0 ? 0 : newIndex);
    onRemoveTab?.(tabs[tabIndex].props.id || '');
  };

  const scrollLeft = () => {
    const { wrapper } = getDomElements();

    if (!wrapper) return;

    wrapper.scrollLeft -= wrapper.offsetWidth;
  };

  const scrollRight = () => {
    const { wrapper } = getDomElements();

    if (!wrapper) return;

    wrapper.scrollLeft += wrapper.offsetWidth;
  };

  const updateScrollPosition = useCallback(() => {
    if (compact) return;

    const { tabs } = getDomElements();

    tabs[currentTabIndex]?.scrollIntoView({ block: 'end', inline: 'nearest' });
  }, [compact, currentTabIndex]);

  const checkArrowsVisibility = useCallback(() => {
    if (compact) return;

    const { tabs, wrapper } = getDomElements();

    if (!wrapper || tabs.length < 2) return;

    const tabsWidth = tabs[0].offsetWidth * tabs.length;

    setArrowsVisible(tabsWidth > wrapper.offsetWidth);
  }, [compact]);

  useEffect(() => setCurrentTabIndex(selectedTabIndex), [selectedTabIndex]);
  useEffect(() => onSelectTab?.(currentTabIndex), [currentTabIndex, onSelectTab]);
  useLayoutEffect(updateScrollPosition, [updateScrollPosition, currentTabIndex]);
  useLayoutEffect(checkArrowsVisibility, [checkArrowsVisibility, tabs.length]);
  useOnResize(mainWrapperRef, checkArrowsVisibility);

  return (
    <div className={classNames(styles.wrapper, typographyStyles.base)} ref={mainWrapperRef} {...props}>
      <nav className={styles.navigation}>
        {arrowsVisible && (
          <button
            type="button"
            aria-label={t('tabs.button.scrollLeft.label')}
            className={styles.button}
            onClick={scrollLeft}
          >
            <ArrowLeftIcon />
          </button>
        )}

        <div
          ref={tabsWrapperRef}
          role="tablist"
          className={classNames(styles.tabs, compact && styles.compact)}
          onAnimationEnd={updateScrollPosition}
        >
          {tabs
            .filter(({ type }) => type === Tab || type === InternalTab)
            .map((tab, index) => (
              <InternalTab
                key={index}
                {...tab.props}
                selected={currentTabIndex === index}
                onClick={() => setCurrentTabIndex(index)}
                onClose={dynamic ? () => removeTab(index) : undefined}
              />
            ))}

          {newTabVisible && <NewTab label={newTabLabel} options={newTabOptions} onOptionSelect={addTab} />}
        </div>

        {newTabButtonVisible && <NewTabButton label={newTabLabel} options={newTabOptions} onOptionSelect={addTab} />}

        {arrowsVisible && (
          <button
            type="button"
            aria-label={t('tabs.button.scrollRight.label')}
            className={styles.button}
            onClick={scrollRight}
          >
            <ArrowRightIcon />
          </button>
        )}
      </nav>

      <div role="tabpanel" className={classNames(styles.content, compact && styles.compact)} {...contentWrapperProps}>
        {tabs[currentTabIndex]?.props.children}
      </div>
    </div>
  );
}
